import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import {
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
  Toolbar,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  EmailOutlined,
} from '@material-ui/icons';
import { workdeskTicketsActions } from '../../store/actions/workdesk-tickets.actions';
import {
  previewingBodyHtmlTicketSelector,
  isManualSiLoadingMapSelector,
} from '../../store/selectors/workdesk-tickets.selector';
import { ReactComponent as AssignTicketLoading } from '../../assets/images/assign-loading.svg';
import { manualSiActions } from '../../store/actions/manual-si.actions';
import { Statuses } from '../../store/models/Statuses';

import PreviewBodyHtmlLinkIframe from '../main/PreviewBodyHtmlLinkIframe';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const name = 'PreviewEmail';

interface PreviewEmailOverlayProps {
  open: boolean;
  handleClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    height: '100%',
  },
}));

const PreviewEmailOverlay: React.FC<PreviewEmailOverlayProps> = (
  props,
) => {
  const dispatch = useDispatch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableDispatch = useCallback(dispatch, []);
  const classes = useStyles();
  const { open, handleClose: handleCloseFromProps } = props;

  // const propsFromDispatch = useSelector(propsFromDispatchSelector);
  const ticket = useSelector(previewingBodyHtmlTicketSelector);
  const { id: ticketId, bodyHtmlLink: ticketBodyHtmlLink } =
    ticket ?? {};

  /**
   * on open the dialog, if no bodyHtmlLink, call fetch link
   */
  useEffect(() => {
    if (open && ticketId && !ticketBodyHtmlLink) {
      stableDispatch(
        workdeskTicketsActions.loadAllBodyHtmls([ticketId]),
      );
    }
  }, [ticketId, ticketBodyHtmlLink, open, stableDispatch]);

  const manualSiLoadingIdMap = useSelector(
    isManualSiLoadingMapSelector,
  );
  const isPostingManualSi = useMemo(
    () =>
      ticket &&
      manualSiLoadingIdMap &&
      manualSiLoadingIdMap[ticket.id],
    [manualSiLoadingIdMap, ticket],
  );
  const [justClickedSiOrNonSi, setJustClickedSiOrNonSi] = useState<
    null | boolean
  >(null);

  const handleClose = () => {
    handleCloseFromProps();
    dispatch(workdeskTicketsActions.closePreviewingBodyHtmlId());
  };

  const handleSINonSI = (siString: string) => {
    const si = JSON.parse(siString);
    setJustClickedSiOrNonSi(si);
    dispatch(manualSiActions.postManualSi(ticket.id, si));
  };

  const disbaled = useMemo(
    () => !ticket || !Statuses.canMarkSiNonSi(ticket.status),
    [ticket],
  );

  if (!ticket) return null;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        className: classes.dialogPaper,
      }}
      maxWidth="lg"
      fullWidth
    >
      <SToolbar>
        <SHeaderLeft>
          <EmailOutlined />
          <Typography variant="h6" color="primary">
            Preview email body
          </Typography>
        </SHeaderLeft>
        <IconButton color="primary" onClick={() => handleClose()}>
          <CloseIcon />
        </IconButton>
      </SToolbar>
      <SContent>
        <SSiRow>
          <SFormLabel>SI:</SFormLabel>
          <SRadioGroup
            value={ticket.si}
            onChange={(e) => handleSINonSI(e.target.value)}
          >
            {[
              { label: 'SI', value: true },
              { label: 'Non-SI', value: false },
            ].map((item) => {
              const isLoading =
                isPostingManualSi &&
                justClickedSiOrNonSi === item.value;
              return (
                <FormControlLabel
                  control={
                    isLoading ? (
                      <SLoadingWrapper>
                        <SAssignTicketLoading />
                      </SLoadingWrapper>
                    ) : (
                      <Radio />
                    )
                  }
                  disabled={disbaled || isLoading}
                  key={item.label}
                  value={item.value}
                  label={item.label}
                />
              );
            })}
          </SRadioGroup>
        </SSiRow>
        <Divider />
        <PreviewBodyHtmlLinkIframe
          ticketId={ticket?.id}
          bodyHtmlLink={ticket?.bodyHtmlLink}
        />
      </SContent>
    </Dialog>
  );
};

export default PreviewEmailOverlay;

const SToolbar = styled(Toolbar)`
  box-shadow: ${(props) => props.theme.boxShadow.appBar};
  display: flex;
  align-items: center;

  color: ${(props) => props.theme.palette.primary.main};
`;
const SHeaderLeft = styled.div`
  display: flex;
  flex: 1;
  column-gap: 1rem;
  align-items: center;
`;
const SContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const SSiRow = styled(FormControl)`
  &.MuiFormControl-root {
    padding: 1rem;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;
const SRadioGroup = styled(RadioGroup)`
  &.MuiFormGroup-root {
    flex-direction: row;
  }
`;
const SFormLabel = styled.div`
  margin: 0 2rem;
`;
const SAssignTicketLoading = styled(AssignTicketLoading)`
  width: 25px;
  height: 25px;
`;
const SLoadingWrapper = styled.div`
  width: 42px;
  overflow: hidden;
`;
